import { ComponentProps, lazy, useEffect, useRef, useState } from "react";
import { SkillTag, ProjectPhoto } from "../../client";
import EditorSkillTagButton from "../../components/atoms/EditorSkillTagButton";
import {
  DynamicInput,
  DynamicInputRef,
} from "../../components/input/DynamicInput";
import { useMemo } from "react";

import { cn } from "../../components/utils/cn";
import { ProjectHeader } from "../dashboard/home/components/link-creator/ProjectHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCirclePlay,
  faEdit,
  faImage,
} from "@fortawesome/free-regular-svg-icons";
import {
  faArrowDown,
  faArrowUp,
  faCirclePlus,
  faClose,
  faRemove,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { ImageButtonLink } from "../../components/navigation-bar/ImageButtonLink";
import { ImageCropperDialog } from "../../components/ImageCropperDialog";
import { createProjectPhoto } from "../../requests";
import { ImageUpload } from "../../types";
import { PhotoModal } from "../../components/PhotoModal";
import { isString } from "../../components";

interface Props extends ComponentProps<typeof ProjectHeader> {
  id: string | undefined;
  description?: string;
  skillTags: SkillTag[];
  showSkills?: boolean;
  showSkillIcons?: boolean;
  showDescription?: boolean;
  isEditable: boolean;
  onWorkplaceEdit: (description: string) => void;
  onWorkplaceContentUpdate?: (title: string, pitch: string | undefined) => void;
  onProjectClickAction?: (action: string) => void;
  projectPhotos?: ProjectPhoto[];
  onImageClick?: (id: string) => void;
  imageUrl?: string;
  isEditing?: boolean;
  pdfMode?: boolean;
  profileId: string;
  onProjectPhotosUpdate?: (newPhoto: ProjectPhoto) => void;
}

const ProfileProject = ({
  id,
  description,
  skillTags,
  showSkills,
  showSkillIcons,
  showDescription,
  isEditable,
  onWorkplaceEdit,
  onWorkplaceContentUpdate,
  onProjectClickAction,
  projectPhotos = [],
  onImageClick,
  imageUrl,
  isEditing,
  pdfMode,
  profileId,
  onProjectPhotosUpdate,
  ...props
}: Props) => {
  const inputRef = useRef<DynamicInputRef>(null);
  const [showActions, setShowActions] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    url: string;
    alt: string;
    assetId: string;
  } | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Adjust textarea height when showDescription changes
  useEffect(() => {
    // Use a small timeout to ensure the element is in the DOM and visible
    if (showDescription && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showDescription]);

  // Adjust textarea height when the description content changes
  useEffect(() => {
    if (description && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [description]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the click is on a button or within the actions menu
      const isButton = target.closest("button");
      const isWithinActions = actionsRef.current?.contains(target);

      if (showActions && !isButton && !isWithinActions) {
        setShowActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  const handleClick = () => {
    if (onProjectClickAction) {
      onProjectClickAction("tags");
    }
  };

  const projectPhotosForThisProject = useMemo(
    () => projectPhotos.filter((photo) => photo.projectId === id),
    [projectPhotos, id],
  );

  const handleImageSelected = (imageUpload: ImageUpload) => {
    if (imageUpload.base64File) {
      setImageToCrop(imageUpload.base64File);
      setShowCropper(true);
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!id) return;

    setIsUploadingPhoto(true);
    const imageUpload: ImageUpload = {
      fileType: "image/jpeg",
      base64File: croppedImage.split(",")[1],
    };

    try {
      const assetId = await createProjectPhoto(profileId, id, imageUpload);
      // Add the new photo to the project photos list immediately
      const newPhoto: ProjectPhoto = {
        assetId,
        projectId: id,
      };
      // Update the local state by adding the new photo to projectPhotos
      if (onProjectPhotosUpdate) {
        onProjectPhotosUpdate(newPhoto);
      }
      if (onProjectClickAction) {
        onProjectClickAction("add-photo");
        // Trigger a refresh to ensure the UI is updated
        onProjectClickAction("refresh-photos");
      }
    } catch (error) {
      console.error("Failed to upload project photo:", error);
    } finally {
      setIsUploadingPhoto(false);
      setShowCropper(false);
    }
  };

  const handlePhotoRemoved = () => {
    if (onProjectClickAction) {
      // First trigger the removal action
      onProjectClickAction("remove-photo");
      // Then trigger an update to refresh the photos
      onProjectClickAction("refresh-photos");
    }
    // Close the modal
    setSelectedPhoto(null);
  };

  return (
    <div className="relative w-full h-full">
      <div className="group/edit relative flex flex-row">
        {isEditable && (
          <div className="absolute left-0 flex flex-col">
            <div className="opacity-0 group-hover/edit:opacity-100 transition-opacity duration-300 pt-1">
              <button
                onClick={() => setShowActions(!showActions)}
                className="cursor-pointer"
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  className={cn(
                    "w-4 h-4 transition-transform duration-200",
                    showActions && "rotate-180",
                  )}
                />
              </button>
            </div>

            {showActions && (
              <div
                ref={actionsRef}
                className="flex flex-col gap-2 bg-white z-[9999] rounded-lg shadow-lg p-1"
                onMouseLeave={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  className={cn(
                    "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
                  )}
                  onClick={() => {
                    setShowActions(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faClose}
                    className="w-4 h-4 text-gray-600"
                  />
                  <span className="text-sm text-gray-700">Close</span>
                </button>
                <button
                  className={cn(
                    "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
                  )}
                  onClick={() => {
                    if (onProjectClickAction) {
                      onProjectClickAction("remove");
                    }
                    setShowActions(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="w-4 h-4 text-red-3"
                  />
                  <span className="text-sm text-gray-700">Delete project</span>
                </button>
                <button
                  className={cn(
                    "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
                  )}
                  onClick={() => {
                    if (onProjectClickAction) {
                      onProjectClickAction("move-up");
                    }
                    setShowActions(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowUp}
                    className="w-4 h-4 text-gray-600"
                  />
                  <span className="text-sm text-gray-700">Move up</span>
                </button>
                <button
                  className={cn(
                    "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
                  )}
                  onClick={() => {
                    if (onProjectClickAction) {
                      onProjectClickAction("move-down");
                    }
                    setShowActions(false);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    className="w-4 h-4 text-gray-600"
                  />
                  <span className="text-sm text-gray-700">Move down</span>
                </button>
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            "flex-grow flex flex-col transition-transform duration-300",
            isEditable && "group-hover/edit:translate-x-6",
          )}
        >
          <div>
            <ProjectHeader
              isEditable={isEditable}
              onHeaderContentUpdate={(title) => {
                if (onWorkplaceContentUpdate)
                  onWorkplaceContentUpdate(title, description);
              }}
              {...props}
            />
            <div className="relative mt-1">
              {isEditable ? (
                <DynamicInput
                  className={cn(
                    "text-xs whitespace-pre-line",
                    "transition-all duration-300 overflow-hidden",
                    showDescription ? "opacity-100" : "max-h-0 opacity-0 pt-0",
                  )}
                  inputClassName="text-sm"
                  disabled={!isEditable}
                  label="Describe the why and how of the project and focus on your contributions and impact"
                  validate={isString}
                  value={description}
                  ref={inputRef}
                  rows={1}
                  editable={isEditable}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    onWorkplaceEdit(inputValue);
                    // Ensure the height is adjusted after editing
                    setTimeout(() => inputRef.current?.adjustHeight(), 0);
                  }}
                />
              ) : (
                <div className="text-sm whitespace-pre-line">{description}</div>
              )}
            </div>
          </div>

          <div>
            <div
              className={cn(
                "flex flex-row flex-wrap overflow-y-auto max-w-full",
                "transition-all duration-300 overflow-visible",
                showSkills && (skillTags?.length > 0 || isEditable)
                  ? "min-h-[3rem] max-h-none opacity-100 pt-1"
                  : "max-h-0 opacity-0 scale-y-0 pt-0",
              )}
            >
              {isEditable && (
                <div
                  className="cursor-pointer flex flex-row justify-center items-center text-sm gap-1 transform transition-transform hover:scale-110 mr-1 group/add-skill"
                  onClick={() =>
                    onProjectClickAction && onProjectClickAction("tags")
                  }
                >
                  <FontAwesomeIcon
                    icon={faCirclePlus}
                    className="w-4 h-4 group-hover/add-skill:scale-125 transition-transform duration-200"
                  />
                  <span className="text-xs">Add skill</span>
                </div>
              )}
              {skillTags?.map((skillTag) => (
                <div
                  key={skillTag.name}
                  className="flex flex-row items-center gap-2"
                >
                  <EditorSkillTagButton
                    profileSkillTag={skillTag}
                    text={skillTag.name}
                    disableEditing={true}
                    handleClick={() => handleClick()}
                    keepLeftPadding={true}
                    isStatic={true}
                    displayInfoOnHover={true}
                    pdfMode={pdfMode}
                    hideIcon={showSkillIcons ? !showSkillIcons : true}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileProject;
